import { reactive } from 'vue';
import { cloneDeep } from 'lodash-es';
import { useNotif } from '@orion.ui/orion';
import { TEMP_WORKER_DOCUMENT_TYPE } from '@/typings/enums/TEMP_WORKER_DOCUMENT_TYPE';
import { sanitizeDate } from '@/utils/tools';
import BaseSetupService from '@/setup/BaseSetupService';
import useProfileCompletionService from '@/services/ProfileCompletionService';
import useUserService from '@/services/user/UserService';

type Props = SetupProps<typeof DashboardProfileCompletionCardSetupService.props>
type Emits = { (e: 'hide'): void }

export default class DashboardProfileCompletionCardSetupService extends BaseSetupService<Props> {
	static readonly props = undefined;

	private emits: Emits;

	private readonly state = reactive({
		user: {} as IUserFullInformationForAppModel,
		profileCompletion: {} as IProfileCompletionModel,
		dataLoaded: false,
	});

	get profileCompletion () { return this.state.profileCompletion; }
	get user () { return this.state.user; }
	get dataLoaded () { return this.state.dataLoaded; }

	get hasStep () {
		if (!this.profileCompletion) return [];
		return this.profileCompletion.tempWorkerDocumentTypes;
	}

	get hasAllDocumentRequired () {
		return (this.hasCurriculumVitae
		&& this.hasDiploma
		&& this.hasExperience
		&& this.hasProfessionalSkill
		&& this.hasIdentityPiece
		&& this.hasSocialCertificate);
	}

	get hasCurriculumVitae () {
		return this.hasStep.includes(TEMP_WORKER_DOCUMENT_TYPE.CurriculumVitae);
	}

	get hasDiploma () {
		return this.hasStep.includes(TEMP_WORKER_DOCUMENT_TYPE.Diploma);
	}

	get hasExperience () {
		return this.state.profileCompletion.hasProfessionalExperience;
	}

	get hasProfessionalSkill () {
		return this.state.profileCompletion.hasProfessionalSkill;
	}

	get hasIdentityPiece () {
		return this.hasStep.includes(TEMP_WORKER_DOCUMENT_TYPE.IdentityPiece);
	}

	get hasSocialCertificate () {
		return this.hasStep.includes(TEMP_WORKER_DOCUMENT_TYPE.SocialCertificate);
	}

	constructor (emits: Emits) {
		super();
		this.emits = emits;
	}

	protected onMounted () {
		super.onMounted();
		this.registerBusEvent('upload', (val: TEMP_WORKER_DOCUMENT_TYPE) => {
			this.updateTempWorkerDocument(val);
			this.updateStateOfProfileCompletionCard();
		});
	}

	protected async onBeforeMountAsync () {
		const data = await useProfileCompletionService().getAsync();
		Object.assign(this.state.profileCompletion, data);

		const user = await useUserService().getUserFullInfosAsync();
		sanitizeDate(user, 'birthDay');
		Object.assign(this.state.user, cloneDeep(user));

		if (this.hasAllDocumentRequired) {
			this.emits('hide');
		} else {
			this.state.dataLoaded = true;
		}
	}

	updateTempWorkerDocument (documentType: TEMP_WORKER_DOCUMENT_TYPE) {
		this.state.profileCompletion.tempWorkerDocumentTypes.push(documentType);
	}

	updateProfessionalExperience () {
		this.state.profileCompletion.hasProfessionalExperience = !this.profileCompletion.hasProfessionalExperience;
	}

	updateProfessionalSkills () {
		this.state.profileCompletion.hasProfessionalSkill = !this.profileCompletion.hasProfessionalSkill;
	}

	updateStateOfProfileCompletionCard () {
		if (this.hasAllDocumentRequired) {
			useNotif.success({
				title: `Félicitations !`,
				message: `Tu as maintenant mis toutes les chances de ton côté pour trouver la mission de tes rêves`,
			});
			this.emits('hide');
		}
	}
}
